import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box, Typography, Paper, Tabs, Tab, IconButton, Chip, TextField,
    InputAdornment, Select, MenuItem, FormControl, InputLabel, Button,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, CircularProgress, Tooltip, Divider, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions, Switch, FormControlLabel,
    Snackbar
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import TerminalIcon from '@mui/icons-material/Terminal';
import StorageIcon from '@mui/icons-material/Storage';
import MemoryIcon from '@mui/icons-material/Memory';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoIcon from '@mui/icons-material/Info';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import FilterListIcon from '@mui/icons-material/FilterList';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import TableChartIcon from '@mui/icons-material/TableChart';
import CodeIcon from '@mui/icons-material/Code';
import DownloadIcon from '@mui/icons-material/Download';
import debugService from '../../services/debugService';
import { useAuth } from '../../context/AuthContext';

// ============= Log Level Colors & Icons =============
const LOG_LEVEL_CONFIG = {
    error: { color: '#ef4444', bg: 'rgba(239,68,68,0.08)', icon: <ErrorOutlineIcon sx={{ fontSize: 14 }} />, label: 'ERROR' },
    warn: { color: '#f59e0b', bg: 'rgba(245,158,11,0.08)', icon: <WarningAmberIcon sx={{ fontSize: 14 }} />, label: 'WARN' },
    info: { color: '#3b82f6', bg: 'rgba(59,130,246,0.08)', icon: <InfoIcon sx={{ fontSize: 14 }} />, label: 'INFO' },
    debug: { color: '#8b5cf6', bg: 'rgba(139,92,246,0.08)', icon: <InfoIcon sx={{ fontSize: 14 }} />, label: 'DEBUG' },
    success: { color: '#10b981', bg: 'rgba(16,185,129,0.08)', icon: <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />, label: 'OK' },
};

function detectLogLevel(line) {
    const lower = line.toLowerCase();
    if (lower.includes('[error]') || lower.includes('error:') || lower.includes('err ')) return 'error';
    if (lower.includes('[warn]') || lower.includes('warning')) return 'warn';
    if (lower.includes('[info]') || lower.includes('info:')) return 'info';
    if (lower.includes('[debug]')) return 'debug';
    if (lower.includes('✅') || lower.includes('successfully') || lower.includes('connected')) return 'success';
    return 'info';
}

// ============= Terminal Log Line Component =============
const LogLine = React.memo(({ line, index, showLineNumbers }) => {
    const level = detectLogLevel(line);
    const config = LOG_LEVEL_CONFIG[level];
    
    // Improved timestamp detection for formats like [2024-04-15T12:00:00Z] or 2024-04-15 12:00:00
    const tsRegex = /(\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}:\d{2}(?:\.\d+)?Z?)/;
    const timestampMatch = line.match(tsRegex);
    let timestamp = timestampMatch ? timestampMatch[1] : null;
    let content = line;

    if (timestamp) {
        // Convert to IST
        try {
            const date = new Date(timestamp.includes('T') ? timestamp : timestamp.replace(' ', 'T'));
            if (!isNaN(date.getTime())) {
                timestamp = date.toLocaleString('en-IN', {
                    timeZone: 'Asia/Kolkata',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: true
                });
            }
        } catch (e) {}
        
        // Remove the raw timestamp from content if it was at the start
        content = line.replace(`[${timestampMatch[1]}]`, '').replace(timestampMatch[1], '').trim();
    }

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                py: 0.3,
                px: 1.5,
                fontFamily: '"JetBrains Mono", "Fira Code", "Cascadia Code", monospace',
                fontSize: '12.5px',
                lineHeight: 1.6,
                backgroundColor: config.bg,
                borderLeft: `3px solid ${config.color}`,
                '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.03)',
                },
                transition: 'background-color 0.15s ease',
            }}
        >
            {showLineNumbers && (
                <Typography
                    component="span"
                    sx={{
                        color: 'rgba(148,163,184,0.5)',
                        fontSize: '11px',
                        minWidth: '36px',
                        textAlign: 'right',
                        userSelect: 'none',
                        fontFamily: 'inherit',
                        flexShrink: 0,
                    }}
                >
                    {index + 1}
                </Typography>
            )}
            {timestamp && (
                <Typography
                    component="span"
                    sx={{
                        color: 'rgba(148,163,184,0.7)',
                        fontSize: '11px',
                        flexShrink: 0,
                        fontFamily: 'inherit',
                    }}
                >
                    {timestamp}
                </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                {config.icon}
            </Box>
            <Typography
                component="span"
                sx={{
                    color: config.color,
                    fontFamily: 'inherit',
                    fontSize: 'inherit',
                    wordBreak: 'break-all',
                    whiteSpace: 'pre-wrap',
                }}
            >
                {content}
            </Typography>
        </Box>
    );
});

// ============= System Info Card =============
const SystemInfoCard = ({ label, value, icon, color = '#3b82f6' }) => (
    <Paper
        elevation={0}
        sx={{
            p: 2,
            borderRadius: '12px',
            background: `linear-gradient(135deg, ${color}10 0%, ${color}05 100%)`,
            border: `1px solid ${color}20`,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flex: '1 1 200px',
        }}
    >
        <Box sx={{
            width: 40, height: 40, borderRadius: '10px',
            background: `${color}15`, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            color: color,
        }}>
            {icon}
        </Box>
        <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '14px' }}>
                {value}
            </Typography>
        </Box>
    </Paper>
);

// ============= Main Component =============
const DebugConsolePage = () => {
    const { user } = useAuth();
    const accessables = user?.accessables ? user.accessables.split(',').map(a => a.trim().toLowerCase().replace(/\s+/g, '')) : [];
    const isDebugOnly = accessables.length === 1 && accessables[0] === 'debug' && user?.role !== 'admin';

    const [activeTab, setActiveTab] = useState(2);
    const [loading, setLoading] = useState(false);

    // === Log State ===
    const [logs, setLogs] = useState({ stdout: null, stderr: null, combined: null });
    const [logSearch, setLogSearch] = useState('');
    const [logLines, setLogLines] = useState(100);
    const [logType, setLogType] = useState('both');
    const [autoRefresh, setAutoRefresh] = useState(false);
    const [showLineNumbers, setShowLineNumbers] = useState(true);
    const [logFilter, setLogFilter] = useState('all'); // all, error, warn, info
    const logEndRef = useRef(null);
    const autoRefreshRef = useRef(null);

    // === Streaming State ===
    const [isStreaming, setIsStreaming] = useState(false);
    const [streamLogs, setStreamLogs] = useState([]);
    const eventSourceRef = useRef(null);

    // === Database State ===
    const [tables, setTables] = useState([]);
    const [selectedTable, setSelectedTable] = useState('');
    const [tableData, setTableData] = useState(null);
    const [tableMeta, setTableMeta] = useState(null);
    const [tablePage, setTablePage] = useState(0);
    const [tableRowsPerPage, setTableRowsPerPage] = useState(50);

    // === Query State ===
    const [sqlQuery, setSqlQuery] = useState('SELECT * FROM devices LIMIT 10;');
    const [queryResult, setQueryResult] = useState(null);
    const [queryError, setQueryError] = useState('');
    const [queryMeta, setQueryMeta] = useState(null);

    // === System State ===
    const [systemInfo, setSystemInfo] = useState(null);

    // === Autopeepal State ===
    const [apeepalLogs, setApeepalLogs] = useState({ lines: [], found: false });
    const [isApeepalStreaming, setIsApeepalStreaming] = useState(false);
    const [apeepalStreamLogs, setApeepalStreamLogs] = useState([]);
    const [apeepalFilter, setApeepalFilter] = useState(''); // device-id filter for live feed + raw log
    const apeepalStreamRef = useRef(null);

    // === Snackbar ===
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // ============= Fetch Logs =============
    const fetchLogs = useCallback(async () => {
        setLoading(true);
        try {
            const result = await debugService.getLogs({
                lines: logLines,
                type: logType,
                search: logSearch,
            });
            if (result.success) {
                setLogs(result.data);
            }
        } catch (err) {
            console.error('Failed to fetch logs:', err);
            setSnackbar({ open: true, message: 'Failed to fetch logs: ' + err.message, severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [logLines, logType, logSearch]);

    // ============= Auto Refresh =============
    useEffect(() => {
        if (autoRefresh && activeTab === 0) {
            autoRefreshRef.current = setInterval(fetchLogs, 5000);
        }
        return () => {
            if (autoRefreshRef.current) clearInterval(autoRefreshRef.current);
        };
    }, [autoRefresh, activeTab, fetchLogs]);

    // ============= Scroll to bottom on new logs =============
    useEffect(() => {
        if (logEndRef.current) {
            logEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [logs, streamLogs]);

    // ============= Streaming =============
    const toggleStreaming = () => {
        if (isStreaming) {
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
                eventSourceRef.current = null;
            }
            setIsStreaming(false);
        } else {
            const es = debugService.createLogStream(
                (data) => {
                    if (data.type !== 'heartbeat') {
                        setStreamLogs(prev => [...prev.slice(-500), data]);
                    }
                },
                () => {
                    setIsStreaming(false);
                    setSnackbar({ open: true, message: 'Log stream disconnected', severity: 'warning' });
                }
            );
            eventSourceRef.current = es;
            setIsStreaming(true);
        }
    };

    // Cleanup streaming on unmount
    useEffect(() => {
        return () => {
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
            }
        };
    }, []);

    // ============= Fetch Database Tables =============
    const fetchTables = async () => {
        setLoading(true);
        try {
            const result = await debugService.getDatabaseTables();
            if (result.success) {
                setTables(result.data);
            }
        } catch (err) {
            console.error('Failed to fetch tables:', err);
            setSnackbar({ open: true, message: 'Failed to fetch tables: ' + err.message, severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // ============= Fetch Table Data =============
    const fetchTableData = async (table, page = 0, limit = 50) => {
        if (!table) return;
        setLoading(true);
        try {
            const result = await debugService.getTableData(table, {
                page: page + 1,
                limit: limit,
                order: 'DESC',
            });
            if (result.success) {
                setTableData(result.data);
                setTableMeta(result.meta);
            }
        } catch (err) {
            console.error('Failed to fetch table data:', err);
            setSnackbar({ open: true, message: 'Failed to fetch table data: ' + err.message, severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // ============= Execute Query =============
    const handleExecuteQuery = async () => {
        if (!sqlQuery.trim()) return;
        setLoading(true);
        setQueryError('');
        setQueryResult(null);
        try {
            const result = await debugService.executeQuery(sqlQuery);
            if (result.success) {
                setQueryResult(result.data);
                setQueryMeta(result.meta);
            } else {
                setQueryError(result.error || 'Query failed');
            }
        } catch (err) {
            setQueryError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // ============= Fetch System Info =============
    const fetchSystemInfo = async () => {
        setLoading(true);
        try {
            const result = await debugService.getSystemInfo();
            if (result.success) {
                setSystemInfo(result.data);
            }
        } catch (err) {
            console.error('Failed to fetch system info:', err);
            setSnackbar({ open: true, message: 'Failed to fetch system info: ' + err.message, severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // ============= Tab Change =============
    useEffect(() => {
        if (activeTab === 0) fetchLogs();
        if (activeTab === 1) fetchTables();
        if (activeTab === 3) fetchSystemInfo();
        if (activeTab === 4) fetchAutopeepalLogs();
    }, [activeTab]);

    // ============= Autopeepal Methods =============
    const fetchAutopeepalLogs = async () => {
        setLoading(true);
        try {
            const limit = isDebugOnly ? 50 : 200;
            const result = await debugService.getAutopeepalLogs(limit);
            if (result.success) {
                setApeepalLogs(result.data);
            }
        } catch (err) {
            console.error('Failed to fetch Autopeepal logs:', err);
        } finally {
            setLoading(false);
        }
    };

    const toggleAutopeepalStreaming = () => {
        if (isApeepalStreaming) {
            if (apeepalStreamRef.current) {
                apeepalStreamRef.current.close();
                apeepalStreamRef.current = null;
            }
            setIsApeepalStreaming(false);
        } else {
            const es = debugService.createAutopeepalStream(
                (data) => {
                    if (data.type !== 'heartbeat') {
                        setApeepalStreamLogs(prev => [...prev.slice(-200), data]);
                    }
                },
                () => {
                    setIsApeepalStreaming(false);
                }
            );
            apeepalStreamRef.current = es;
            setIsApeepalStreaming(true);
        }
    };

    // ============= Copy Logs =============
    const copyLogs = (logData) => {
        if (!logData?.lines) return;
        const text = logData.lines.join('\n');
        navigator.clipboard.writeText(text);
        setSnackbar({ open: true, message: 'Logs copied to clipboard', severity: 'success' });
    };

    // ============= Download Logs =============
    const downloadLogs = (logData, filename) => {
        if (!logData?.lines) return;
        const text = logData.lines.join('\n');
        const blob = new Blob([text], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || 'logs.txt';
        a.click();
        URL.revokeObjectURL(url);
    };

    // ============= Filter log lines by level =============
    const filterLines = (lines) => {
        if (!lines || logFilter === 'all') return lines;
        return lines.filter(line => {
            const level = detectLogLevel(line);
            return level === logFilter;
        });
    };

    // ============= Render Log Panel =============
    const renderLogPanel = (logData, title, type) => {
        if (!logData) return null;
        if (!logData.found) {
            return (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px' }}>
                    Log file not found: {logData.file || 'unknown'}. This is normal if the backend is not running on this machine.
                </Alert>
            );
        }

        const filteredLines = filterLines(logData.lines) || [];
        const errorCount = logData.lines?.filter(l => detectLogLevel(l) === 'error').length || 0;
        const warnCount = logData.lines?.filter(l => detectLogLevel(l) === 'warn').length || 0;

        return (
            <Paper
                elevation={0}
                sx={{
                    mb: 2,
                    borderRadius: '12px',
                    border: '1px solid rgba(148,163,184,0.15)',
                    overflow: 'hidden',
                    backgroundColor: '#0f172a',
                }}
            >
                {/* Terminal Header */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1,
                    background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
                    borderBottom: '1px solid rgba(148,163,184,0.1)',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {/* macOS-style dots */}
                        <Box sx={{ display: 'flex', gap: 0.7 }}>
                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#ef4444' }} />
                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#f59e0b' }} />
                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#22c55e' }} />
                        </Box>
                        <Typography sx={{ color: 'rgba(148,163,184,0.8)', fontSize: '12px', fontWeight: 600, fontFamily: 'monospace' }}>
                            {title}
                        </Typography>
                        <Chip label={`${filteredLines.length} lines`} size="small" sx={{
                            height: 20, fontSize: '10px', fontWeight: 600,
                            backgroundColor: 'rgba(59,130,246,0.15)', color: '#60a5fa',
                        }} />
                        {errorCount > 0 && (
                            <Chip label={`${errorCount} errors`} size="small" sx={{
                                height: 20, fontSize: '10px', fontWeight: 600,
                                backgroundColor: 'rgba(239,68,68,0.15)', color: '#f87171',
                            }} />
                        )}
                        {warnCount > 0 && (
                            <Chip label={`${warnCount} warnings`} size="small" sx={{
                                height: 20, fontSize: '10px', fontWeight: 600,
                                backgroundColor: 'rgba(245,158,11,0.15)', color: '#fbbf24',
                            }} />
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Copy all">
                            <IconButton size="small" onClick={() => copyLogs(logData)} sx={{ color: 'rgba(148,163,184,0.6)', '&:hover': { color: '#60a5fa' } }}>
                                <ContentCopyIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Download">
                            <IconButton size="small" onClick={() => downloadLogs(logData, `${type}_logs.txt`)} sx={{ color: 'rgba(148,163,184,0.6)', '&:hover': { color: '#60a5fa' } }}>
                                <DownloadIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Terminal Body */}
                <Box sx={{
                    maxHeight: '400px',
                    overflowY: 'auto',
                    overflowX: 'auto',
                    '&::-webkit-scrollbar': { width: '6px', height: '6px' },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': { background: 'rgba(148,163,184,0.2)', borderRadius: '3px' },
                    '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(148,163,184,0.4)' },
                }}>
                    {filteredLines.length === 0 ? (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                            <Typography sx={{ color: 'rgba(148,163,184,0.5)', fontSize: '13px', fontFamily: 'monospace' }}>
                                No log entries found
                            </Typography>
                        </Box>
                    ) : (
                        // Reverse logs to show newest at top
                        [...filteredLines].reverse().map((line, idx) => (
                            <LogLine key={idx} line={line} index={idx} showLineNumbers={showLineNumbers} />
                        ))
                    )}
                    <div ref={logEndRef} />
                </Box>
            </Paper>
        );
    };

    // ============= TAB 0: Console Logs =============
    const renderLogsTab = () => (
        <Box>
            {/* Controls Bar */}
            <Paper elevation={0} sx={{
                p: 2, mb: 2, borderRadius: '12px',
                border: '1px solid rgba(148,163,184,0.12)',
                display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center',
            }}>
                <TextField
                    size="small"
                    placeholder="Search logs..."
                    value={logSearch}
                    onChange={(e) => setLogSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && fetchLogs()}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ flex: '1 1 200px', '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '13px' } }}
                />

                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel sx={{ fontSize: '13px' }}>Log Type</InputLabel>
                    <Select value={logType} onChange={(e) => setLogType(e.target.value)} label="Log Type" sx={{ borderRadius: '10px', fontSize: '13px' }}>
                        <MenuItem value="both">All Logs</MenuItem>
                        <MenuItem value="out">Stdout Only</MenuItem>
                        <MenuItem value="error">Stderr Only</MenuItem>
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel sx={{ fontSize: '13px' }}>Lines</InputLabel>
                    <Select value={logLines} onChange={(e) => setLogLines(e.target.value)} label="Lines" sx={{ borderRadius: '10px', fontSize: '13px' }}>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                        <MenuItem value={200}>200</MenuItem>
                        <MenuItem value={500}>500</MenuItem>
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel sx={{ fontSize: '13px' }}>Level</InputLabel>
                    <Select value={logFilter} onChange={(e) => setLogFilter(e.target.value)} label="Level" sx={{ borderRadius: '10px', fontSize: '13px' }}>
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="error">Errors</MenuItem>
                        <MenuItem value="warn">Warnings</MenuItem>
                        <MenuItem value="info">Info</MenuItem>
                    </Select>
                </FormControl>

                <FormControlLabel
                    control={<Switch checked={showLineNumbers} onChange={(e) => setShowLineNumbers(e.target.checked)} size="small" />}
                    label={<Typography sx={{ fontSize: '12px' }}>Line #</Typography>}
                />

                <FormControlLabel
                    control={<Switch checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} size="small" color="success" />}
                    label={<Typography sx={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Auto {autoRefresh && <FiberManualRecordIcon sx={{ fontSize: 8, color: '#22c55e', animation: 'pulse 1.5s infinite' }} />}
                    </Typography>}
                />

                <Tooltip title={isStreaming ? 'Stop live stream' : 'Start live stream'}>
                    <Button
                        variant={isStreaming ? 'contained' : 'outlined'}
                        size="small"
                        startIcon={isStreaming ? <StopIcon /> : <PlayArrowIcon />}
                        onClick={toggleStreaming}
                        color={isStreaming ? 'error' : 'primary'}
                        sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, fontSize: '12px' }}
                    >
                        {isStreaming ? 'Stop Stream' : 'Live Stream'}
                    </Button>
                </Tooltip>

                <Tooltip title="Refresh logs">
                    <IconButton onClick={fetchLogs} disabled={loading} sx={{ color: 'primary.main' }}>
                        <RefreshIcon sx={{ fontSize: 20, animation: loading ? 'spin 1s linear infinite' : 'none' }} />
                    </IconButton>
                </Tooltip>
            </Paper>

            {/* Stream Output */}
            {isStreaming && streamLogs.length > 0 && (
                <Paper elevation={0} sx={{
                    mb: 2, borderRadius: '12px', overflow: 'hidden',
                    border: '1px solid rgba(16,185,129,0.3)', backgroundColor: '#0f172a',
                }}>
                    <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        px: 2, py: 1, background: 'linear-gradient(180deg, #064e3b 0%, #0f172a 100%)',
                        borderBottom: '1px solid rgba(16,185,129,0.2)',
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FiberManualRecordIcon sx={{ fontSize: 10, color: '#22c55e', animation: 'pulse 1s infinite' }} />
                            <Typography sx={{ color: '#6ee7b7', fontSize: '12px', fontWeight: 700, fontFamily: 'monospace' }}>
                                LIVE STREAM
                            </Typography>
                            <Chip label={`${streamLogs.length} events`} size="small" sx={{ height: 18, fontSize: '10px', backgroundColor: 'rgba(16,185,129,0.2)', color: '#6ee7b7' }} />
                        </Box>
                        <Tooltip title="Clear stream">
                            <IconButton size="small" onClick={() => setStreamLogs([])} sx={{ color: 'rgba(148,163,184,0.6)' }}>
                                <DeleteSweepIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Box sx={{ maxHeight: '250px', overflowY: 'auto', overflowX: 'auto' }}>
                        {streamLogs.map((entry, idx) => (
                            <LogLine key={idx} line={`[${entry.type}] ${entry.line || entry.message}`} index={idx} showLineNumbers={false} />
                        ))}
                    </Box>
                </Paper>
            )}

            {/* Log Panels */}
            {logs.stdout && renderLogPanel(logs.stdout, '📋 stdout — web-backend-out.log', 'stdout')}
            {logs.stderr && renderLogPanel(logs.stderr, '🚨 stderr — web-backend-error.log', 'stderr')}
            {logs.combined && renderLogPanel(logs.combined, '📝 combined — winston combined.log', 'combined')}

            {!logs.stdout && !logs.stderr && !logs.combined && !loading && (
                <Alert severity="info" sx={{ borderRadius: '12px' }}>
                    Click <b>Refresh</b> to load backend console logs. The logs are read from PM2 log files on the server.
                </Alert>
            )}
        </Box>
    );

    // ============= TAB 1: Database Explorer =============
    const renderDatabaseTab = () => (
        <Box>
            <Paper elevation={0} sx={{
                p: 2, mb: 2, borderRadius: '12px',
                border: '1px solid rgba(148,163,184,0.12)',
                display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center',
            }}>
                <FormControl size="small" sx={{ minWidth: 250 }}>
                    <InputLabel sx={{ fontSize: '13px' }}>Select Table</InputLabel>
                    <Select
                        value={selectedTable}
                        onChange={(e) => {
                            setSelectedTable(e.target.value);
                            setTablePage(0);
                            fetchTableData(e.target.value, 0, tableRowsPerPage);
                        }}
                        label="Select Table"
                        sx={{ borderRadius: '10px', fontSize: '13px' }}
                    >
                        {tables.map(table => (
                            <MenuItem key={table} value={table}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <TableChartIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    {table}
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Chip label={`${tables.length} tables found`} sx={{
                    backgroundColor: 'rgba(59,130,246,0.1)', color: '#3b82f6',
                    fontWeight: 600, fontSize: '12px',
                }} />

                <Tooltip title="Refresh tables list">
                    <IconButton onClick={fetchTables} disabled={loading}>
                        <RefreshIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </Tooltip>
            </Paper>

            {/* Table Data */}
            {tableData && (
                <Paper elevation={0} sx={{
                    borderRadius: '12px', overflow: 'hidden',
                    border: '1px solid rgba(148,163,184,0.12)',
                }}>
                    <Box sx={{
                        px: 2, py: 1.5,
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        background: 'linear-gradient(135deg, rgba(59,130,246,0.05) 0%, transparent 100%)',
                        borderBottom: '1px solid rgba(148,163,184,0.1)',
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <StorageIcon sx={{ fontSize: 18, color: '#3b82f6' }} />
                            <Typography sx={{ fontWeight: 700, fontSize: '14px' }}>{selectedTable}</Typography>
                            {tableMeta && (
                                <Chip label={`${tableMeta.total} rows`} size="small" sx={{
                                    height: 22, fontSize: '11px', fontWeight: 600,
                                    backgroundColor: 'rgba(16,185,129,0.1)', color: '#10b981',
                                }} />
                            )}
                        </Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {tableData.columns?.length} columns
                        </Typography>
                    </Box>

                    <TableContainer sx={{
                        maxHeight: 500,
                        '&::-webkit-scrollbar': { width: '6px', height: '6px' },
                        '&::-webkit-scrollbar-thumb': { background: 'rgba(148,163,184,0.2)', borderRadius: '3px' },
                    }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    {tableData.columns?.map(col => (
                                        <TableCell key={col.name} sx={{
                                            fontWeight: 700, fontSize: '11px',
                                            textTransform: 'uppercase', letterSpacing: '0.5px',
                                            backgroundColor: 'background.paper',
                                            borderBottom: '2px solid',
                                            borderColor: 'divider',
                                            whiteSpace: 'nowrap',
                                        }}>
                                            <Tooltip title={`${col.type}${col.key ? ` (${col.key})` : ''}`}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    {col.key === 'PRI' && <span style={{ color: '#f59e0b' }}>🔑</span>}
                                                    {col.name}
                                                </Box>
                                            </Tooltip>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tableData.rows?.map((row, idx) => (
                                    <TableRow key={idx} hover sx={{ '&:hover': { backgroundColor: 'rgba(59,130,246,0.03)' } }}>
                                        {tableData.columns?.map(col => (
                                            <TableCell key={col.name} sx={{
                                                fontSize: '12px', maxWidth: '300px',
                                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                fontFamily: 'monospace',
                                            }}>
                                                <Tooltip title={String(row[col.name] ?? 'NULL')} placement="top">
                                                    <span>{row[col.name] !== null && row[col.name] !== undefined ? String(row[col.name]) : <em style={{ color: '#94a3b8' }}>NULL</em>}</span>
                                                </Tooltip>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {tableMeta && (
                        <TablePagination
                            component="div"
                            count={tableMeta.total || 0}
                            page={tablePage}
                            onPageChange={(e, newPage) => {
                                setTablePage(newPage);
                                fetchTableData(selectedTable, newPage, tableRowsPerPage);
                            }}
                            rowsPerPage={tableRowsPerPage}
                            onRowsPerPageChange={(e) => {
                                const newLimit = parseInt(e.target.value, 10);
                                setTableRowsPerPage(newLimit);
                                setTablePage(0);
                                fetchTableData(selectedTable, 0, newLimit);
                            }}
                            rowsPerPageOptions={[25, 50, 100, 200]}
                            sx={{
                                borderTop: '1px solid rgba(148,163,184,0.1)',
                                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                                    fontSize: '12px',
                                },
                            }}
                        />
                    )}
                </Paper>
            )}

            {!tableData && !loading && (
                <Alert severity="info" sx={{ borderRadius: '12px', mt: 2 }}>
                    Select a table from the dropdown to view its data.
                </Alert>
            )}
        </Box>
    );

    // ============= TAB 2: SQL Query =============
    const renderQueryTab = () => (
        <Box>
            <Paper elevation={0} sx={{
                p: 2, mb: 2, borderRadius: '12px',
                border: '1px solid rgba(148,163,184,0.12)',
            }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 2 }}>
                    <CodeIcon sx={{ mt: 1, color: '#8b5cf6' }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '14px', mt: 0.7 }}>SQL Query Editor</Typography>
                    <Chip label="READ & WRITE" size="small" sx={{
                        height: 20, fontSize: '10px', fontWeight: 700,
                        backgroundColor: 'rgba(16,185,129,0.1)', color: '#10b981',
                        mt: 0.7,
                    }} />
                </Box>

                <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={sqlQuery}
                    onChange={(e) => setSqlQuery(e.target.value)}
                    placeholder="Enter SQL query (SELECT, INSERT, UPDATE, DELETE, SHOW, DESCRIBE)..."
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                            handleExecuteQuery();
                        }
                    }}
                    sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '10px',
                            fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                            fontSize: '13px',
                            backgroundColor: '#0f172a',
                            color: '#e2e8f0',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(148,163,184,0.2)',
                        },
                    }}
                />

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Button
                        variant="contained"
                        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                        onClick={handleExecuteQuery}
                        disabled={loading || !sqlQuery.trim()}
                        sx={{
                            borderRadius: '10px', textTransform: 'none',
                            fontWeight: 700, fontSize: '13px',
                            background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                            '&:hover': { background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)' },
                        }}
                    >
                        Execute (Ctrl+Enter)
                    </Button>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Supports all SQL statements — SELECT, INSERT, UPDATE, DELETE, etc.
                    </Typography>
                </Box>
            </Paper>

            {queryError && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>
                    {queryError}
                </Alert>
            )}

            {queryResult && (
                <Paper elevation={0} sx={{
                    borderRadius: '12px', overflow: 'hidden',
                    border: '1px solid rgba(148,163,184,0.12)',
                }}>
                    <Box sx={{
                        px: 2, py: 1.5,
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        background: 'linear-gradient(135deg, rgba(139,92,246,0.05) 0%, transparent 100%)',
                        borderBottom: '1px solid rgba(148,163,184,0.1)',
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircleOutlineIcon sx={{ fontSize: 18, color: '#22c55e' }} />
                            <Typography sx={{ fontWeight: 700, fontSize: '14px' }}>Query Results</Typography>
                            {queryMeta && (
                                <>
                                    {queryMeta.isWriteQuery ? (
                                        <>
                                            <Chip label={`${queryMeta.affectedRows} rows affected`} size="small" sx={{ height: 22, fontSize: '11px', fontWeight: 600, backgroundColor: 'rgba(16,185,129,0.1)', color: '#10b981' }} />
                                            {queryMeta.insertId > 0 && (
                                                <Chip label={`Insert ID: ${queryMeta.insertId}`} size="small" sx={{ height: 22, fontSize: '11px', fontWeight: 600, backgroundColor: 'rgba(59,130,246,0.1)', color: '#3b82f6' }} />
                                            )}
                                        </>
                                    ) : (
                                        <Chip label={`${queryMeta.rowCount} rows`} size="small" sx={{ height: 22, fontSize: '11px', fontWeight: 600, backgroundColor: 'rgba(16,185,129,0.1)', color: '#10b981' }} />
                                    )}
                                    <Chip label={queryMeta.duration} size="small" sx={{ height: 22, fontSize: '11px', fontWeight: 600, backgroundColor: 'rgba(139,92,246,0.1)', color: '#8b5cf6' }} />
                                </>
                            )}
                        </Box>
                    </Box>

                    {queryMeta?.isWriteQuery ? (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                            <CheckCircleOutlineIcon sx={{ fontSize: 40, color: '#22c55e', mb: 1 }} />
                            <Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#22c55e', mb: 0.5 }}>
                                Query Executed Successfully
                            </Typography>
                            <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                                {queryMeta.affectedRows} row(s) affected
                                {queryMeta.insertId > 0 && ` • Insert ID: ${queryMeta.insertId}`}
                                {` • Duration: ${queryMeta.duration}`}
                            </Typography>
                        </Box>
                    ) : (
                        <TableContainer sx={{ maxHeight: 500 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        {queryResult.length > 0 && Object.keys(queryResult[0]).map(key => (
                                            <TableCell key={key} sx={{
                                                fontWeight: 700, fontSize: '11px',
                                                textTransform: 'uppercase', letterSpacing: '0.5px',
                                                backgroundColor: 'background.paper',
                                                borderBottom: '2px solid', borderColor: 'divider',
                                                whiteSpace: 'nowrap',
                                            }}>{key}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {queryResult.map((row, idx) => (
                                        <TableRow key={idx} hover>
                                            {Object.values(row).map((val, colIdx) => (
                                                <TableCell key={colIdx} sx={{
                                                    fontSize: '12px', fontFamily: 'monospace',
                                                    maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                }}>
                                                    {val !== null && val !== undefined ? String(val) : <em style={{ color: '#94a3b8' }}>NULL</em>}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Paper>
            )}
        </Box>
    );

    // ============= TAB 3: System Info =============
    const renderSystemTab = () => (
        <Box>
            {systemInfo ? (
                <>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                        <SystemInfoCard label="Uptime" value={systemInfo.uptimeFormatted} icon={<MemoryIcon sx={{ fontSize: 20 }} />} color="#3b82f6" />
                        <SystemInfoCard label="Node Version" value={systemInfo.nodeVersion} icon={<CodeIcon sx={{ fontSize: 20 }} />} color="#22c55e" />
                        <SystemInfoCard label="Environment" value={systemInfo.env?.NODE_ENV || 'N/A'} icon={<TerminalIcon sx={{ fontSize: 20 }} />} color="#f59e0b" />
                        <SystemInfoCard label="CPUs" value={`${systemInfo.os?.cpus || 'N/A'} cores`} icon={<MemoryIcon sx={{ fontSize: 20 }} />} color="#8b5cf6" />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        {/* Memory Info */}
                        <Paper elevation={0} sx={{ p: 2.5, borderRadius: '12px', border: '1px solid rgba(148,163,184,0.12)', flex: '1 1 300px' }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '14px', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <MemoryIcon sx={{ fontSize: 18, color: '#3b82f6' }} /> Process Memory
                            </Typography>
                            {Object.entries(systemInfo.memory || {}).map(([key, val]) => (
                                <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.8, borderBottom: '1px solid rgba(148,163,184,0.08)' }}>
                                    <Typography sx={{ fontSize: '13px', color: 'text.secondary', textTransform: 'capitalize' }}>{key.replace(/([A-Z])/g, ' $1')}</Typography>
                                    <Typography sx={{ fontSize: '13px', fontWeight: 600, fontFamily: 'monospace' }}>{val}</Typography>
                                </Box>
                            ))}
                        </Paper>

                        {/* OS Info */}
                        <Paper elevation={0} sx={{ p: 2.5, borderRadius: '12px', border: '1px solid rgba(148,163,184,0.12)', flex: '1 1 300px' }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '14px', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <StorageIcon sx={{ fontSize: 18, color: '#22c55e' }} /> System Info
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.8, borderBottom: '1px solid rgba(148,163,184,0.08)' }}>
                                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>Hostname</Typography>
                                <Typography sx={{ fontSize: '13px', fontWeight: 600, fontFamily: 'monospace' }}>{systemInfo.os?.hostname}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.8, borderBottom: '1px solid rgba(148,163,184,0.08)' }}>
                                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>Platform</Typography>
                                <Typography sx={{ fontSize: '13px', fontWeight: 600, fontFamily: 'monospace' }}>{systemInfo.platform} ({systemInfo.arch})</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.8, borderBottom: '1px solid rgba(148,163,184,0.08)' }}>
                                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>Total Memory</Typography>
                                <Typography sx={{ fontSize: '13px', fontWeight: 600, fontFamily: 'monospace' }}>{systemInfo.os?.totalMemory}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.8, borderBottom: '1px solid rgba(148,163,184,0.08)' }}>
                                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>Free Memory</Typography>
                                <Typography sx={{ fontSize: '13px', fontWeight: 600, fontFamily: 'monospace' }}>{systemInfo.os?.freeMemory}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.8, borderBottom: '1px solid rgba(148,163,184,0.08)' }}>
                                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>DB Host</Typography>
                                <Typography sx={{ fontSize: '13px', fontWeight: 600, fontFamily: 'monospace' }}>{systemInfo.env?.DB_HOST}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.8 }}>
                                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>DB Name</Typography>
                                <Typography sx={{ fontSize: '13px', fontWeight: 600, fontFamily: 'monospace' }}>{systemInfo.env?.DB_NAME}</Typography>
                            </Box>
                        </Paper>
                    </Box>
                </>
            ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    {loading ? <CircularProgress /> : (
                        <Button onClick={fetchSystemInfo} variant="outlined" startIcon={<RefreshIcon />} sx={{ borderRadius: '10px', textTransform: 'none' }}>
                            Load System Info
                        </Button>
                    )}
                </Box>
            )}
        </Box>
    );

    // ============= TAB 4: Autopeepal Data =============
    const renderAutopeepalTab = () => (
        <Box>
            <Paper elevation={0} sx={{
                p: 2, mb: 2, borderRadius: '12px',
                border: '1px solid rgba(148,163,184,0.12)',
                display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center',
                background: 'rgba(59,130,246,0.03)'
            }}>
                <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '14px', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FiberManualRecordIcon sx={{ fontSize: 16, color: '#06b6d4' }} /> Autopeepal Device MQTT Feed
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Listening on <b>localhost:8883</b> (Autopeepal MQTT Bridge)
                    </Typography>
                </Box>

                {/* Device-ID filter — show only frames whose topic/payload
                    contains this id (applies to live feed + raw log below). */}
                <TextField
                    size="small"
                    placeholder="Filter by Device ID / IMEI…"
                    value={apeepalFilter}
                    onChange={(e) => setApeepalFilter(e.target.value.trim())}
                    sx={{ minWidth: 240, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                            </InputAdornment>
                        ),
                        endAdornment: apeepalFilter ? (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setApeepalFilter('')}><ClearIcon sx={{ fontSize: 16 }} /></IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                />

                <Button
                    variant={isApeepalStreaming ? 'contained' : 'outlined'}
                    startIcon={isApeepalStreaming ? <StopIcon /> : <PlayArrowIcon />}
                    onClick={toggleAutopeepalStreaming}
                    color={isApeepalStreaming ? 'error' : 'primary'}
                    sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
                >
                    {isApeepalStreaming ? 'Stop Live Feed' : 'Start Live Feed'}
                </Button>

                <IconButton onClick={fetchAutopeepalLogs} disabled={loading}>
                    <RefreshIcon sx={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
                </IconButton>
            </Paper>

            {/* Live Feed Terminal */}
            {isApeepalStreaming && (
                <Paper elevation={0} sx={{
                    mb: 2, borderRadius: '12px', overflow: 'hidden',
                    border: '1px solid #06b6d440', backgroundColor: '#0f172a',
                }}>
                    <Box sx={{
                        px: 2, py: 1, backgroundColor: '#06b6d420',
                        borderBottom: '1px solid #06b6d420',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                    }}>
                        <Typography sx={{ color: '#06b6d4', fontSize: '11px', fontWeight: 800, fontFamily: 'monospace' }}>
                            REAL-TIME MQTT STREAM{apeepalFilter ? ` · ${apeepalFilter}` : ''}
                        </Typography>
                        {(() => {
                            const shown = apeepalFilter
                                ? apeepalStreamLogs.filter(l => (l.line || '').toLowerCase().includes(apeepalFilter.toLowerCase()))
                                : apeepalStreamLogs;
                            return <Chip label={`${shown.length}${apeepalFilter ? ` / ${apeepalStreamLogs.length}` : ''} events`} size="small" sx={{ height: 18, fontSize: '10px', backgroundColor: '#06b6d420', color: '#06b6d4' }} />;
                        })()}
                    </Box>
                    <Box sx={{ maxHeight: '300px', overflowY: 'auto', p: 1 }}>
                        {(() => {
                            const shown = apeepalFilter
                                ? apeepalStreamLogs.filter(l => (l.line || '').toLowerCase().includes(apeepalFilter.toLowerCase()))
                                : apeepalStreamLogs;
                            if (shown.length === 0) {
                                return (
                                    <Typography sx={{ p: 2, color: 'rgba(148,163,184,0.4)', fontSize: '12px', textAlign: 'center', fontFamily: 'monospace' }}>
                                        {apeepalFilter ? `No frames from "${apeepalFilter}" yet…` : 'Waiting for device data...'}
                                    </Typography>
                                );
                            }
                            // Reverse stream logs to show newest at top
                            return [...shown].reverse().map((log, idx) => {
                                let istTime = '';
                                try {
                                    const date = new Date(log.timestamp);
                                    istTime = date.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour12: true });
                                } catch (e) {
                                  istTime = log.timestamp?.split('T')[1]?.split('.')[0] || '';
                                }
                                return (
                                    <Box key={idx} sx={{ py: 0.2, borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                                        <Typography sx={{ fontFamily: 'monospace', fontSize: '12px', color: '#06b6d4' }}>
                                            <span style={{ opacity: 0.5 }}>[{istTime}]</span> {log.line}
                                        </Typography>
                                    </Box>
                                );
                            });
                        })()}
                        <div ref={logEndRef} />
                    </Box>
                </Paper>
            )}

            {/* Static Log Reader — also honours the device-id filter */}
            {renderLogPanel(
                apeepalFilter
                    ? { ...apeepalLogs, lines: (apeepalLogs.lines || []).filter(l => String(l).toLowerCase().includes(apeepalFilter.toLowerCase())) }
                    : apeepalLogs,
                '📜 Autopeepal Raw MQTT Logs — autopeepal_raw.log', 'autopeepal'
            )}
        </Box>
    );

    return (
        <Box sx={{ p: 3, maxWidth: '1400px', margin: '0 auto' }}>
            {/* Tabs */}
            <Paper elevation={0} sx={{
                mb: 3, borderRadius: '12px',
                border: '1px solid rgba(148,163,184,0.12)',
                overflow: 'hidden',
            }}>
                <Tabs
                    value={activeTab}
                    onChange={(e, v) => setActiveTab(v)}
                    variant="fullWidth"
                    sx={{
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '13px',
                            py: 1.5,
                            minHeight: 48,
                        },
                        '& .Mui-selected': {
                            color: '#3b82f6 !important',
                        },
                        '& .MuiTabs-indicator': {
                            height: 3,
                            borderRadius: '3px 3px 0 0',
                            backgroundColor: '#3b82f6',
                        },
                    }}
                >
                    {!isDebugOnly && <Tab icon={<TerminalIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Console Logs" />}
                    {!isDebugOnly && <Tab icon={<StorageIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Database Explorer" />}
                    {!isDebugOnly && <Tab icon={<CodeIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="SQL Query" />}
                    {!isDebugOnly && <Tab icon={<MemoryIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="System Info" />}
                    <Tab icon={<FiberManualRecordIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Autopeepal Data" />
                </Tabs>
            </Paper>

            {/* Loading Overlay */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <CircularProgress size={24} />
                </Box>
            )}

            {/* Tab Content */}
            {!isDebugOnly && activeTab === 0 && renderLogsTab()}
            {!isDebugOnly && activeTab === 1 && renderDatabaseTab()}
            {!isDebugOnly && activeTab === 2 && renderQueryTab()}
            {!isDebugOnly && activeTab === 3 && renderSystemTab()}
            {(isDebugOnly ? activeTab === 0 : activeTab === 4) && renderAutopeepalTab()}

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ borderRadius: '10px', fontWeight: 600 }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Pulse animation for live indicators */}
            <style>{`
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.3; }
                }
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </Box>
    );
};

export default DebugConsolePage;
